import React, { useState, useMemo, useEffect } from "react";
import { Search, Building2, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAllTasksAllBrands } from "@/hooks/useAllTasksAllBrands";
import { useMomsAllBrands } from "@/hooks/useMomsAllBrands";
import { useNavigate } from "react-router-dom";
import { useBrandManager } from "@/hooks/useBrandManager";

interface TaskSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type SearchItemType = 'foundational' | 'growth' | 'operations_mom' | 'sales_mom';

interface SearchItem {
  id: string;
  type: SearchItemType;
  brandId: string;
  brandName: string;
  displayTitle: string;
  displayDescription?: string;
  displayStatus: string;
}

export const TaskSearchModal: React.FC<TaskSearchModalProps> = ({ isOpen, onClose }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [submittedQuery, setSubmittedQuery] = useState("");
  const navigate = useNavigate();
  const { selectBrand, currentBrandId } = useBrandManager();
  
  // Only fetch data after user submits a search
  const shouldFetch = isOpen && submittedQuery.trim().length > 0;
  
  const { foundationalTasks, growthTasks, isLoading: isLoadingTasks } = useAllTasksAllBrands({ enabled: shouldFetch });
  const { operationsMoms, salesMoms, isLoading: isLoadingMoms } = useMomsAllBrands({ enabled: shouldFetch });

  const isLoading = isLoadingTasks || isLoadingMoms;

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
      setSubmittedQuery("");
    }
  }, [isOpen]);

  const handleSearch = () => {
    if (searchQuery.trim()) {
      setSubmittedQuery(searchQuery.trim());
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Combine all items into unified search array
  const allItems = useMemo(() => {
    const items: SearchItem[] = [];

    // Foundational tasks
    foundationalTasks.forEach(task => {
      items.push({
        id: task.id,
        type: 'foundational',
        brandId: task.brand?.id || task.brand_id || '',
        brandName: task.brand?.name || 'Unknown Brand',
        displayTitle: task.title,
        displayDescription: task.description,
        displayStatus: task.status
      });
    });

    // Growth tasks
    growthTasks.forEach(task => {
      items.push({
        id: task.id,
        type: 'growth',
        brandId: task.brand?.id || task.brand_id || '',
        brandName: task.brand?.name || 'Unknown Brand',
        displayTitle: task.name,
        displayDescription: task.description,
        displayStatus: task.task_status || 'pending'
      });
    });

    // Operations MOMs
    operationsMoms.forEach(mom => {
      items.push({
        id: mom.id,
        type: 'operations_mom',
        brandId: mom.brand?.id || mom.brand_id,
        brandName: mom.brand?.name || 'Unknown Brand',
        displayTitle: mom.filename,
        displayDescription: mom.summary || mom.transcript_content?.substring(0, 200),
        displayStatus: mom.status
      });
    });

    // Sales MOMs
    salesMoms.forEach(mom => {
      items.push({
        id: mom.id,
        type: 'sales_mom',
        brandId: mom.brand?.id || mom.brand_id,
        brandName: mom.brand?.name || 'Unknown Brand',
        displayTitle: mom.client_name,
        displayDescription: mom.description || mom.transcript_content?.substring(0, 200),
        displayStatus: mom.status
      });
    });

    return items;
  }, [foundationalTasks, growthTasks, operationsMoms, salesMoms]);

  // Filter items based on submitted query
  const filteredItems = useMemo(() => {
    if (!submittedQuery.trim()) return [];
    
    const query = submittedQuery.toLowerCase();
    return allItems.filter(item => 
      item.displayTitle?.toLowerCase().includes(query) ||
      item.displayDescription?.toLowerCase().includes(query) ||
      item.brandName?.toLowerCase().includes(query)
    );
  }, [allItems, submittedQuery]);

  const getTypeLabel = (type: SearchItemType) => {
    switch (type) {
      case 'foundational': return 'Task';
      case 'growth': return 'Growth';
      case 'operations_mom': return 'Ops MOM';
      case 'sales_mom': return 'Sales MOM';
    }
  };

  const getTypeColor = (type: SearchItemType) => {
    switch (type) {
      case 'foundational':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case 'growth':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'operations_mom':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300';
      case 'sales_mom':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'done':
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'in_progress':
      case 'processing':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'pending':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
    }
  };

  const handleItemClick = async (item: SearchItem) => {
    const brandId = item.brandId;
    
    // Switch brand if needed
    if (brandId && currentBrandId !== brandId) {
      await selectBrand(brandId);
    }

    // Navigate based on type
    switch (item.type) {
      case 'foundational':
      case 'growth':
        if (currentBrandId !== brandId) {
          window.location.href = `/tasks/${brandId}/${item.id}`;
        } else {
          navigate(`/tasks/${brandId}/${item.id}`);
        }
        break;
      case 'operations_mom':
        if (currentBrandId !== brandId) {
          window.location.href = '/documentation-hub/project-moms';
        } else {
          navigate('/documentation-hub/project-moms');
        }
        break;
      case 'sales_mom':
        if (currentBrandId !== brandId) {
          window.location.href = '/sales-operations/moms';
        } else {
          navigate('/sales-operations/moms');
        }
        break;
    }
    
    onClose();
  };

  const taskCount = filteredItems.filter(i => i.type === 'foundational' || i.type === 'growth').length;
  const momCount = filteredItems.filter(i => i.type === 'operations_mom' || i.type === 'sales_mom').length;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Search Across All Brands
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search tasks, operations MOMs, and sales MOMs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              className="pl-9"
              autoFocus
            />
          </div>
          <Button onClick={handleSearch} disabled={!searchQuery.trim()}>
            Search
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {!submittedQuery.trim() ? (
            <div className="flex flex-col items-center justify-center py-8">
              <Search className="h-8 w-8 text-muted-foreground/50 mb-2" />
              <div className="text-muted-foreground">Type your search and press Enter or click Search</div>
            </div>
          ) : isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-muted-foreground">Searching...</div>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-muted-foreground">No results found for "{submittedQuery}"</div>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredItems.map((item) => (
                <Card 
                  key={`${item.type}-${item.id}`} 
                  className="hover:bg-muted/50 transition-colors cursor-pointer"
                  onClick={() => handleItemClick(item)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          {(item.type === 'operations_mom' || item.type === 'sales_mom') && (
                            <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                          )}
                          <h4 className="font-medium truncate">{item.displayTitle}</h4>
                          <Badge variant="secondary" className={getTypeColor(item.type)}>
                            {getTypeLabel(item.type)}
                          </Badge>
                          <Badge variant="outline" className={getStatusColor(item.displayStatus)}>
                            {item.displayStatus?.replace('_', ' ')}
                          </Badge>
                        </div>
                        
                        {item.displayDescription && (
                          <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                            {item.displayDescription}
                          </p>
                        )}
                        
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Building2 className="h-3 w-3" />
                            <span>{item.brandName}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
        
        {submittedQuery.trim() && !isLoading && filteredItems.length > 0 && (
          <div className="text-xs text-muted-foreground text-center pt-2">
            Found {taskCount} task{taskCount !== 1 ? 's' : ''} and {momCount} MOM{momCount !== 1 ? 's' : ''}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
